import { useEffect, useRef } from 'react';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { logOperationalEvent } from 'saddlebag-logger';

import getFeatureDecision from '../../../../../config';
import { COMBO_RECOMMENDATION_TYPE } from '../../../../../config/constants';
import { logWarnWrapper } from '../../../../../services/logWrapper';
import {
  setMichelinRecommendationsRequested,
  setMichelinRecommendationsReceived,
  setMichelinRecommendationsFailed,
  setMichelinRecommendationsReset,
} from '../../../actions/michelin-actions';
import { COMPONENT_CAPYBARA_RECOMMENDATIONS } from '../../../helpers/constants/analyticsConstants';
import {
  OUTBOUND_DIRECTION,
  INBOUND_DIRECTION,
} from '../../../helpers/constants/dataTypeConstants';
import {
  getGridPriceData,
  getMonthDataUrl,
  getSelectedOutboundDate,
  getSelectedInboundDate,
  getOriginEntityId,
  getDestinationEntityId,
  getTripDays,
  getPreferDirects,
  isLoading,
} from '../../../selectors';
import {
  getMichelinRecommendationsRequest,
  getMichelinRecommendationsStatus,
} from '../../../selectors/michelinSelectors';
import { getRecommendations } from '../../clients/michelin';

import type { State } from '../../../selectors/types';
import type {
  SkyscannerMichelinCommonVerticalV1Vertical,
  SkyscannerMichelinRecommendationsV1Recommendationtypes,
} from '@skyscanner-internal/michelin-web-http-client';

const RECOMMENDATIONS_TIMEOUT_MS = 10_000;
// Hotels for stays longer than 29 days are unreliable (often missing or
// returning a single result), so we skip combo recommendations beyond this.
const MAX_COMBO_TRIP_DAYS = 29;
const VERTICALS = [
  'VERTICAL_FLIGHTS',
  'VERTICAL_HOTELS',
  'VERTICAL_CAR_HIRE',
] as SkyscannerMichelinCommonVerticalV1Vertical.Vertical[];

type UseRecommendationsOptions = {
  enabled?: boolean;
};

export const useRecommendations = ({
  enabled = true,
}: UseRecommendationsOptions = {}) => {
  const dispatch = useDispatch();
  const store = useStore<State>();
  const selectedOutbound = useSelector(getSelectedOutboundDate);
  const selectedInbound = useSelector(getSelectedInboundDate);
  const originEntityId = useSelector(getOriginEntityId);
  const destinationEntityId = useSelector(getDestinationEntityId);
  const preferDirects = useSelector(getPreferDirects);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const tripDays = useSelector(getTripDays);

  const RECOMMENDATION_TYPE: SkyscannerMichelinRecommendationsV1Recommendationtypes.RecommendationType =
    getFeatureDecision(COMBO_RECOMMENDATION_TYPE, 'cheapest') === 'best'
      ? 'RECOMMENDATION_TYPE_BEST'
      : 'RECOMMENDATION_TYPE_CHEAPEST';

  useEffect(() => {
    const isTripTooLong = tripDays > MAX_COMBO_TRIP_DAYS;

    if (
      !enabled ||
      !selectedOutbound ||
      !selectedInbound ||
      selectedOutbound >= selectedInbound ||
      isTripTooLong
    ) {
      if (isTripTooLong) {
        logOperationalEvent({
          component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
          eventName: 'skipped_long_trip',
          tripDays: String(tripDays),
        });
      }

      const status = getMichelinRecommendationsStatus(store.getState());
      if (status !== 'idle') {
        requestIdRef.current += 1;
        abortControllerRef.current?.abort();
        dispatch(setMichelinRecommendationsReset());
      }
      return undefined;
    }

    const state = store.getState();
    const gridLoaded = !isLoading(state) && getMonthDataUrl(state) !== '';

    if (gridLoaded) {
      const outboundPrice = getGridPriceData(
        state,
        selectedOutbound,
        OUTBOUND_DIRECTION,
      ).price;
      const inboundPrice = getGridPriceData(
        state,
        selectedInbound,
        INBOUND_DIRECTION,
      ).price;

      if (outboundPrice === null || inboundPrice === null) {
        logOperationalEvent({
          component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
          eventName: 'no_calendar_price',
          hasOutboundPrice: outboundPrice === null ? '0' : '1',
          hasInboundPrice: inboundPrice === null ? '0' : '1',
        });

        const status = getMichelinRecommendationsStatus(store.getState());
        if (status !== 'idle') {
          requestIdRef.current += 1;
          abortControllerRef.current?.abort();
          dispatch(setMichelinRecommendationsReset());
        }

        return undefined;
      }
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const timeoutId = setTimeout(
      () => abortController.abort(),
      RECOMMENDATIONS_TIMEOUT_MS,
    );

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    const isStale = () => currentRequestId !== requestIdRef.current;

    const request = getMichelinRecommendationsRequest(
      store.getState(),
      VERTICALS,
      RECOMMENDATION_TYPE,
    );

    dispatch(setMichelinRecommendationsRequested());

    const doFetch = async () => {
      try {
        const response = await getRecommendations(
          request,
          abortController.signal,
        );

        if (isStale()) return;

        // Validate that the recommendation contains the required data for a combo card.
        // buildComboRecommendation requires both flight AND hotel results with valid data.
        // Note: totalPrice can be null - formatMoney() handles this by returning ''.
        const { recommendation } = response;
        const hasFlightResult = recommendation?.verticalResults?.some(
          (v) => v?.flightResult?.outbound,
        );
        const hasInboundFlight = recommendation?.verticalResults?.some(
          (v) => v?.flightResult?.inbound,
        );
        const hasHotelResult = recommendation?.verticalResults?.some(
          (v) => v.hotelResult,
        );
        const hasValidData =
          recommendation && hasFlightResult && hasHotelResult;

        logOperationalEvent({
          component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
          eventName: 'recommendation_validation',
          hasRecommendation: recommendation ? '1' : '0',
          hasVerticalResults:
            recommendation?.verticalResults &&
            recommendation.verticalResults.length > 0
              ? '1'
              : '0',
          hasFlightResult: hasFlightResult ? '1' : '0',
          hasOutboundFlight: hasFlightResult ? '1' : '0',
          hasInboundFlight: hasInboundFlight ? '1' : '0',
          hasHotelResult: hasHotelResult ? '1' : '0',
          verticalResultsCount: String(
            recommendation?.verticalResults?.length || 0,
          ),
          isValid: hasValidData ? '1' : '0',
        });

        if (hasValidData) {
          dispatch(setMichelinRecommendationsReceived(recommendation));
        } else {
          dispatch(setMichelinRecommendationsFailed());
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;
        if (isStale()) return;

        dispatch(setMichelinRecommendationsFailed());

        logWarnWrapper({
          message: 'useRecommendations fetch error',
          component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
        });
      } finally {
        clearTimeout(timeoutId);
      }
    };

    doFetch();

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [
    enabled,
    selectedOutbound,
    selectedInbound,
    originEntityId,
    destinationEntityId,
    preferDirects,
    dispatch,
    store,
    tripDays,
    RECOMMENDATION_TYPE,
  ]);
};
